import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import Skeleton from "react-loading-skeleton";
import EntityInfoV2View from "../../common/EntityInfoV2View";
import CheckList from "../../CheckList";
import SubSiteList from "../../SubSiteList";
import FreezerSessionList from "./FreezerSessionList";

const TAB_KEYS = new Set(["checklist", "response", "site-list"]);

const FreezerFrostInfo = () => {
  const { uuid } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const userData = useSelector((state) => state.userSlice);
  const configData = useSelector((state) => state.configSlice);
  const [row, setRow] = useState(null);
  const [machine, setMachine] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialTab = TAB_KEYS.has(searchParams.get("tab")) ? searchParams.get("tab") : "checklist";
  const [tab, setTab] = useState(initialTab);
  const [phase, setPhase] = useState("entry");
  const [questionCount, setQuestionCount] = useState(0);
  const [responseCount, setResponseCount] = useState(0);
  const [tabActions, setTabActions] = useState(null);

  useEffect(() => {
    const nextTab = searchParams.get("tab");
    if (TAB_KEYS.has(nextTab)) {
      setTab(nextTab);
    } else {
      setTab("checklist");
    }
  }, [searchParams]);

  useEffect(() => {
    const load = async () => {
      try {
        const headers = { Authorization: `Bearer ${userData.token}` };
        const [detail, machineRes] = await Promise.all([
          axios.get(`${configData.config.API_URL}/api/freezer-frost/detail/${uuid}`, { headers }),
          axios.get(`${configData.config.API_URL}/api/machine/details/${uuid}`, { headers }),
        ]);
        setRow(detail.data.data);
        setMachine(machineRes.data.data);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };
    if (uuid && configData?.config?.API_URL && userData?.token) load();
  }, [uuid, configData?.config?.API_URL, userData?.token]);

  const groupId = useMemo(() => {
    const fromMachine = Array.isArray(machine?.groups) ? machine.groups.find(Boolean) : null;
    const fromRow = Array.isArray(row?.groups) ? row.groups.find(Boolean) : null;
    return fromMachine || fromRow || userData?.user?.group?.uuid || null;
  }, [machine?.groups, row?.groups, userData?.user?.group?.uuid]);

  const handleTabChange = (nextTab) => {
    setTab(nextTab);
    setTabActions(null);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (nextTab === "checklist") next.delete("tab");
        else next.set("tab", nextTab);
        return next;
      },
      { replace: true },
    );
  };

  if (loading) {
    return (
      <div className="sites-v2">
        <Skeleton height={36} width={280} />
      </div>
    );
  }

  if (!row) return <div className="sites-v2">Freezer not found</div>;

  return (
    <EntityInfoV2View
      navigate={navigate}
      loading={false}
      entityName={row.name}
      editPath={`/ff/edit/${uuid}`}
      backPath={groupId ? `/ff/list/view/${groupId}` : undefined}
      thumbnail={row.thumbnail || machine?.thumbnail}
      responseCount={responseCount}
      questionCount={questionCount}
      serialNo={machine?.data?.serialNo || row.serialNo}
      entityIdLabel="Freezer ID"
      entityId={machine?.data?.machineId || row.machineId}
      tags={machine?.tags || []}
      tabs={[
        { key: "checklist", label: "Questionnaire" },
        { key: "response", label: "User Response" },
        { key: "site-list", label: "Site List" },
      ]}
      activeTab={tab}
      onTabChange={handleTabChange}
      tabActions={tabActions}
    >
      {tab === "checklist" && (
        <>
          <div className="ff-phase">
            <div className="ff-phase__switch" role="tablist" aria-label="Question set">
              <button
                type="button"
                className={`ff-phase__btn ${phase === "entry" ? "is-active" : ""}`}
                onClick={() => setPhase("entry")}
              >
                Entry questions
              </button>
              <button
                type="button"
                className={`ff-phase__btn ${phase === "exit" ? "is-active" : ""}`}
                onClick={() => setPhase("exit")}
              >
                Exit questions
              </button>
            </div>
            <p className="ff-phase__hint">
              {phase === "entry"
                ? "Shown after the Entry QR scan, before the user goes in."
                : "Shown after the Exit QR scan, when the user is leaving."}
            </p>
          </div>
          <CheckList
            key={phase}
            phase={phase}
            setQestionLength={setQuestionCount}
            setQuestionMapping={() => {}}
            setTabActions={setTabActions}
          />
        </>
      )}
      {tab === "response" && (
        <FreezerSessionList setResponseLenght={setResponseCount} setTabActions={setTabActions} />
      )}
      {tab === "site-list" && (
        <SubSiteList
          variant="v2"
          siteId={uuid}
          setTabActions={setTabActions}
          emptyTitle="No locations yet"
          emptyText="Add the places inside this freezer. On the phone they appear after the QR scan, the same way as a site list."
        />
      )}
    </EntityInfoV2View>
  );
};

export default FreezerFrostInfo;
